// runCache.js
//
// WHY THIS EXISTS (client item: "Persistence", Sagar's refresh bug)
// ────────────────────────────────────────────────────────────────────────
// fittingSessionStore.js / fitRecordStore.js are module-level singletons.
// They survive route changes but NOT a browser refresh — a refresh reloads
// the JS bundle, every module variable resets, and the app lands back on
// the Examples page with every result gone.
//
// This keeps the most recent runs — INPUTS AND RESULTS TOGETHER — in
// localStorage so they can be rehydrated after a refresh:
//   • the last 5 EXAMPLE runs
//   • the last 10 USER fit runs
// Example runs have their own bucket, so they never count against the
// user's 10 (matches the cap discussed on the call).
//
// WIRING (the two calls DataFittingPage / the Examples page need):
//   1. when a run finishes:   pushRun('example' | 'user', { id, label, inputs, result })
//   2. on mount:              const last = listRuns('user')[0]  → refill form + results
//
// Scoped per user (decoded from the JWT) so two accounts on one browser
// never see each other's runs, and wiped by clearRunCache() on logout.
// Storage is best-effort: quota errors evict the oldest entries instead of
// throwing, and every access is try/catch'd (private mode, disabled storage).

const KEY_PREFIX = 'kq_run_cache_v1';

export const CACHE_LIMITS = { example: 5, user: 10 };

// Stable id for the logged-in user, read from the JWT payload. Falls back to
// 'anon' — still isolated from real accounts, just shared among anonymous use.
export function currentUserId() {
  try {
    const token = localStorage.getItem('token');
    const part = token && token.split('.')[1];
    if (!part) return 'anon';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return String(json.sub ?? json.user_id ?? json.id ?? json.email ?? 'anon');
  } catch (e) {
    return 'anon';
  }
}

const storageKey = () => `${KEY_PREFIX}:${currentUserId()}`;

function read() {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      example: Array.isArray(parsed && parsed.example) ? parsed.example : [],
      user: Array.isArray(parsed && parsed.user) ? parsed.user : [],
    };
  } catch (e) {
    return { example: [], user: [] };
  }
}

function write(data) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(data));
    return true;
  } catch (e) {
    return false;
  }
}

// entry: { id, label, system_name?, inputs, result }
// Upserts by id (re-running the same example replaces it), newest first,
// trimmed to the bucket limit. Returns true if the entry ended up stored.
export function pushRun(kind, entry) {
  if (!CACHE_LIMITS[kind] || !entry || entry.id == null) return false;
  const data = read();
  const record = { ...entry, kind, saved_at: new Date().toISOString() };
  data[kind] = [record, ...data[kind].filter((r) => r.id !== entry.id)]
    .slice(0, CACHE_LIMITS[kind]);

  // Results can be large (predicted time series). If the browser refuses the
  // write, shed the oldest OTHER entry (never the one being saved) and retry.
  while (!write(data)) {
    const others = [
      ...data.example.filter((r) => r !== record),
      ...data.user.filter((r) => r !== record),
    ];
    if (!others.length) return false;            // nothing left to shed: give up quietly
    // Oldest saved_at first. Timestamps can tie (two saves in one millisecond),
    // and each bucket is already newest-first, so on a tie the entry that sits
    // LATER in its list is the older one.
    const oldest = others
      .map((r, i) => ({ r, i }))
      .sort((a, b) => (new Date(a.r.saved_at || 0) - new Date(b.r.saved_at || 0)) || (b.i - a.i))[0].r;
    data.example = data.example.filter((r) => r !== oldest);
    data.user = data.user.filter((r) => r !== oldest);
  }
  return true;
}

export function listRuns(kind) {
  return read()[kind] || [];
}

export function getRun(kind, id) {
  return listRuns(kind).find((r) => r.id === id) || null;
}

export function removeRun(kind, id) {
  const data = read();
  if (!data[kind]) return;
  data[kind] = data[kind].filter((r) => r.id !== id);
  write(data);
}

// Call from the logout handler, next to resetFitRecordState().
export function clearRunCache() {
  try { localStorage.removeItem(storageKey()); } catch (e) { /* ignore */ }
}
